import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import "leaflet.heat";
import { http, API_BASE } from "@/lib/api";
import ShareButton from "@/components/ShareButton";
import { FireSimple, MapTrifold, DotsSix } from "@phosphor-icons/react";

// Fix Leaflet default icon URLs
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const abs = (u) => (!u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`);
const escape = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

// A curated set of common topic chips shown at the top of the map.
// Order matters — most-shown-first. Slugs align with backend NEWS_CATEGORIES.
const TOPIC_CHIPS = [
  { slug: "wildfires", label: "Wildfires", emoji: "🔥", color: "#c2410c" },
  { slug: "floods", label: "Floods", emoji: "🌊", color: "#0284c7" },
  { slug: "landslide", label: "Landslides", emoji: "⛰️", color: "#a16207" },
  { slug: "geological", label: "Geological", emoji: "🌋", color: "#7c2d12" },
  { slug: "weather", label: "Weather", emoji: "🌦️", color: "#0891b2" },
  { slug: "climate", label: "Climate", emoji: "🌡️", color: "#be123c" },
  { slug: "wildlife", label: "Wildlife", emoji: "🦋", color: "#059669" },
  { slug: "insects", label: "Insects", emoji: "🐝", color: "#65a30d" },
  { slug: "trees", label: "Trees", emoji: "🌳", color: "#16a34a" },
  { slug: "oceans", label: "Oceans", emoji: "🌊", color: "#0e7490" },
  { slug: "freshwater", label: "Freshwater", emoji: "💧", color: "#0891b2" },
  { slug: "drought", label: "Drought", emoji: "🏜️", color: "#a16207" },
  { slug: "agriculture", label: "Agriculture", emoji: "🌾", color: "#ca8a04" },
  { slug: "conservation", label: "Conservation", emoji: "🌿", color: "#15803d" },
  { slug: "health", label: "Health", emoji: "🩺", color: "#be123c" },
  { slug: "energy", label: "Energy", emoji: "⚡", color: "#0284c7" },
];

const KIND_COLOR = {
  observation: "#059669",
  news_link: "#B85F2A",
};

// Marker layer that runs inside a MapContainer.
function PinLayer({ pins }) {
  const map = useMap();
  useEffect(() => {
    if (!pins || pins.length === 0) return;
    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      maxClusterRadius: 45,
    });
    pins.forEach((p) => {
      const isNews = p.kind === "news_link";
      const color = isNews ? KIND_COLOR.news_link : KIND_COLOR.observation;
      const marker = L.circleMarker([p.lat, p.lon], {
        radius: isNews ? 6 : 8,
        color,
        weight: 2,
        fillColor: color,
        fillOpacity: p.is_derived ? 0.45 : 0.78,
        dashArray: p.is_derived ? "3 3" : undefined,
      });
      const badge = `<div style="display:inline-block; padding:2px 6px; border-radius:999px; background:${color}22; color:${color}; font-family:ui-monospace; font-size:9px; letter-spacing:.24em; text-transform:uppercase;">${isNews ? "news · " : ""}${escape(p.sub_kind || (isNews ? "" : "observation"))}</div>`;
      const cover = p.image
        ? `<img src="${abs(p.image)}" onerror="this.style.display='none'" style="width:100%;height:150px;object-fit:cover;border-radius:8px;margin-bottom:6px;display:block" />`
        : "";
      const publisher = isNews && p.publisher
        ? `<div style="font-family:ui-monospace; font-size:9px; letter-spacing:.22em; text-transform:uppercase; color:#3A3830; margin-top:3px">${escape(p.publisher)}</div>` : "";
      const approxNote = p.is_derived
        ? `<div style="font-family:ui-monospace; font-size:9px; letter-spacing:.22em; text-transform:uppercase; color:#3A3830; margin-top:2px">Approx · ${escape(p.precision || "country")} centroid</div>` : "";
      const summary = p.summary
        ? `<div style="font-size:11px; color:#3A3830; margin-top:6px; line-height:1.35">${escape(p.summary)}</div>` : "";
      const location = p.display_name
        ? `<div style="font-size:11px; color:#3A3830; margin-top:4px">📍 ${escape(p.display_name)}</div>` : "";
      const openBtn = `<a href="${p.href}" style="display:inline-block; margin-top:8px; font-family:ui-monospace; font-size:10px; letter-spacing:.22em; text-transform:uppercase; color:#1B4332; border-bottom:1px solid #1B4332">Open ${isNews ? "story" : "sighting"} →</a>`;
      const externalBtn = isNews && p.external_url
        ? `<a href="${p.external_url}" target="_blank" rel="noopener noreferrer" style="display:inline-block; margin-left:12px; margin-top:8px; font-family:ui-monospace; font-size:10px; letter-spacing:.22em; text-transform:uppercase; color:#B85F2A; border-bottom:1px solid #B85F2A">Source ↗</a>` : "";
      marker.bindPopup(`
        <div style="font-family: ui-serif, Georgia; min-width: 240px; max-width: 300px">
          ${cover}
          ${badge}
          <div style="font-size:14px; line-height:1.25; margin-top:6px; font-weight:500">${escape(p.title || "")}</div>
          ${publisher}
          ${location}
          ${approxNote}
          ${summary}
          <div>${openBtn}${externalBtn}</div>
        </div>
      `, { maxWidth: 320 });
      group.addLayer(marker);
    });
    map.addLayer(group);
    if (pins.length > 0) {
      try { map.fitBounds(group.getBounds(), { padding: [40, 40], maxZoom: 6 }); } catch { /* ignore */ }
    }
    return () => { map.removeLayer(group); };
  }, [pins, map]);
  return null;
}

// Heatmap layer — leaflet.heat gradient over all pins.
function HeatLayer({ pins }) {
  const map = useMap();
  useEffect(() => {
    if (!pins || pins.length === 0) return;
    // Weight observations more (real, precise) than derived news pins.
    const points = pins.map((p) => [p.lat, p.lon, p.kind === "observation" ? 1.0 : 0.6]);
    const layer = L.heatLayer(points, {
      radius: 28, blur: 22, maxZoom: 6, minOpacity: 0.35,
      gradient: { 0.2: "#0284c7", 0.4: "#16a34a", 0.6: "#ca8a04", 0.8: "#c2410c", 1.0: "#be123c" },
    });
    layer.addTo(map);
    return () => { map.removeLayer(layer); };
  }, [pins, map]);
  return null;
}

export default function ObservationsMap() {
  const [sp, setSp] = useSearchParams();
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const filter = sp.get("kind") || "all";
  const cat = sp.get("category") || "";
  const view = sp.get("view") || "pins"; // "pins" | "heat"

  useEffect(() => {
    const types = filter === "all" ? "observation,news_link"
                : filter === "news_link" ? "news_link"
                : "observation";
    const params = new URLSearchParams({ types, limit: "500" });
    if (cat) params.set("category", cat);
    setLoading(true);
    http.get(`/nature/map/pins?${params}`)
      .then((r) => {
        setPins(r.data?.pins || []);
        setStats(r.data?.stats || null);
      })
      .catch(() => setPins([]))
      .finally(() => setLoading(false));
  }, [filter, cat]);

  const setKind = (k) => setSp((sp) => {
    const n = new URLSearchParams(sp);
    if (k === "all") n.delete("kind"); else n.set("kind", k);
    return n;
  });

  const toggleCategory = (slug) => setSp((sp) => {
    const n = new URLSearchParams(sp);
    const current = new Set((n.get("category") || "").split(",").filter(Boolean));
    if (current.has(slug)) current.delete(slug); else current.add(slug);
    if (current.size === 0) n.delete("category"); else n.set("category", Array.from(current).join(","));
    return n;
  });

  const activeCats = useMemo(() => new Set(cat.split(",").filter(Boolean)), [cat]);

  const setView = (v) => setSp((sp) => {
    const n = new URLSearchParams(sp);
    if (v === "pins") n.delete("view"); else n.set("view", v);
    return n;
  });

  const obsCount = pins.filter((p) => p.kind === "observation").length;
  const newsCount = pins.filter((p) => p.kind === "news_link").length;

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[color:var(--np-text,#1B4332)] min-h-screen pt-32 md:pt-36 pb-32" data-testid="map-page">
      <section className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">
          / NaturePulse live map
        </div>
        <h1 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
          Where the planet is being watched.
        </h1>
        <p className="mt-3 font-serif-display text-base md:text-lg text-[#3A3830] max-w-2xl">
          Emerald dots are field observations with precise GPS. Orange dots are news stories placed on the map — dashed rings mean the point is approximate.
        </p>

        {/* Kind + View controls */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {[
            { key: "all", label: `All · ${pins.length}` },
            { key: "observation", label: `Observations · ${obsCount}` },
            { key: "news_link", label: `News · ${newsCount}` },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setKind(f.key)}
              data-testid={`map-filter-${f.key}`}
              className={`rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase border ${
                filter === f.key ? "bg-[#1B4332] text-[#F5EBDC] border-[#1B4332]" : "border-black/20 hover:bg-white/60"
              }`}
            >{f.label}</button>
          ))}

          {/* View toggle: pins ↔ heatmap */}
          <div className="ml-2 inline-flex rounded-full border border-black/20 bg-white/40 p-0.5" role="tablist" aria-label="View">
            <button
              onClick={() => setView("pins")}
              data-testid="map-view-pins"
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase ${
                view === "pins" ? "bg-[#1B4332] text-[#F5EBDC]" : "text-[#1B4332] hover:bg-white/70"
              }`}
              aria-selected={view === "pins"}
            ><DotsSix size={11} weight="bold" /> Pins</button>
            <button
              onClick={() => setView("heat")}
              data-testid="map-view-heat"
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase ${
                view === "heat" ? "bg-[#1B4332] text-[#F5EBDC]" : "text-[#1B4332] hover:bg-white/70"
              }`}
              aria-selected={view === "heat"}
            ><FireSimple size={11} weight="bold" /> Heatmap</button>
          </div>

          <span className="ml-auto font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)]">
            {loading ? "loading…" : `${pins.length} ${view === "heat" ? "signals" : "pins"}`}
          </span>
          <ShareButton title="NaturePulse Live Map" text="Every dot is a location-tagged observation or news story." variant="chip" testid="map-share" />
        </div>

        {/* Topic chips */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="map-topics">
          <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] mr-1">Topics ·</span>
          {activeCats.size > 0 && (
            <button
              onClick={() => setSp((sp) => { const n = new URLSearchParams(sp); n.delete("category"); return n; })}
              data-testid="map-topic-clear"
              className="rounded-full border border-black/20 bg-white/60 px-2.5 py-0.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#1B4332] hover:bg-white"
            >× Clear</button>
          )}
          {TOPIC_CHIPS.map((t) => {
            const on = activeCats.has(t.slug);
            const inResults = stats?.categories?.[t.slug] || 0;
            return (
              <button
                key={t.slug}
                onClick={() => toggleCategory(t.slug)}
                data-testid={`map-topic-${t.slug}`}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase transition-colors ${
                  on
                    ? "border-transparent text-white"
                    : "border-black/15 bg-white/40 text-[#3A3830] hover:bg-white/70"
                }`}
                style={on ? { background: t.color } : {}}
              >
                <span aria-hidden="true">{t.emoji}</span>
                {t.label}
                {inResults > 0 && !on && <span className="ml-1 opacity-60">{inResults}</span>}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-3 flex flex-wrap items-center gap-4 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)]">
          {view === "pins" ? (
            <>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-full" style={{ background: "#059669" }} />
                Observation · GPS-exact
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-full border-2 border-dashed" style={{ background: "#B85F2A88", borderColor: "#B85F2A" }} />
                News · approx (city/state/country)
              </span>
            </>
          ) : (
            <span className="inline-flex items-center gap-2">
              <MapTrifold size={12} weight="fill" /> Heatmap intensity: cool → hot means more nearby signals
            </span>
          )}
          {stats?.news_link_skipped ? (
            <span className="opacity-70">· {stats.news_link_skipped} news items couldn't be pinned (no country)</span>
          ) : null}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-6">
        <div className="rounded-2xl overflow-hidden border border-black/10 bg-black/5" style={{ height: "70vh" }} data-testid="observations-map">
          <MapContainer
            center={[20, 0]} zoom={2} scrollWheelZoom
            style={{ width: "100%", height: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {view === "pins" ? <PinLayer pins={pins} /> : <HeatLayer pins={pins} />}
          </MapContainer>
        </div>
      </section>
    </main>
  );
}
